/**
 * AIGateHits — /admin/ai-gate-hits. Aggregates every feature-gated AI
 * Ambassador call over the last N days. Shows admins the top questions
 * Free users tried that got gated, plus estimated MRR left on the table —
 * an evidence-based upsell dataset.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import ToolSelector from '../../components/ToolSelector';
import {
  ArrowLeft, Lock, TrendingUp, Users as UsersIcon, HelpCircle, DollarSign,
  Loader2, Info,
} from 'lucide-react';

const RANGES = [
  { label: '7d',  value: 7 },
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
];

const money = (n) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

const Stat = ({ icon: Icon, label, value, hint, testid }) => (
  <div className="pro-card p-4 flex items-start gap-3" data-testid={testid}>
    <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
      <div className="font-heading text-2xl font-extrabold">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  </div>
);

const AIGateHits = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/ai-gate-hits?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Could not load gate hit report'))
      .finally(() => setLoading(false));
  }, [API, days]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="ai-gate-hits-page">
      <ToolSelector />
      <div className="max-w-[1200px] mx-auto px-4 py-6">
        <Button variant="ghost" onClick={() => navigate('/admin')} className="mb-3 rounded-full h-8 text-xs">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to Admin
        </Button>

        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <span className="eyebrow">Admin · AI Support</span>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">
              Gate Hit Report
            </h1>
            <p className="text-muted-foreground text-sm max-w-2xl mt-1">
              Feature-gated questions Free / Pro members tried to ask the AI Ambassador. Use this
              as evidence-based upsell copy — the top questions here are what your users actually
              want to solve today.
            </p>
          </div>
          <div className="flex items-center gap-1 rounded-full border border-border bg-card p-1">
            {RANGES.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setDays(r.value)}
                data-testid={`range-${r.label}`}
                className={`text-[11px] font-bold uppercase tracking-widest px-2.5 h-7 rounded-full transition-colors ${
                  days === r.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-muted-foreground text-sm inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> loading…
          </div>
        ) : !data ? null : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-6">
              <Stat icon={Lock}       label="Gate hits"        value={data.total_gate_hits}      hint={`in last ${data.days} days`}       testid="stat-hits" />
              <Stat icon={UsersIcon}  label="Unique users"     value={data.unique_gated_users}   hint="hit at least one gate"             testid="stat-users" />
              <Stat icon={DollarSign} label="MRR left on table" value={money(data.estimated_mrr_at_5pct)} hint={`assuming ${Math.round(data.conversion_assumption * 100)}% conversion`} testid="stat-mrr" />
              <Stat icon={TrendingUp} label="Top gate topic"    value={Object.entries(data.by_topic || {}).sort((a,b)=>b[1]-a[1])[0]?.[0] || '—'} hint={`${Object.entries(data.by_topic||{}).sort((a,b)=>b[1]-a[1])[0]?.[1] || 0} hits`} testid="stat-topic" />
            </div>

            {/* Top gated questions */}
            <section className="mb-6">
              <h2 className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
                Top gated questions ({data.top_questions?.length || 0})
              </h2>
              {data.top_questions?.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground text-center flex items-center justify-center gap-2">
                  <Info className="w-4 h-4" /> No gate hits in this window yet.
                </div>
              ) : (
                <div className="pro-card overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                      <tr>
                        <th className="text-left p-3">Sample question</th>
                        <th className="text-right p-3 w-20">Hits</th>
                        <th className="text-right p-3 w-24">Users</th>
                        <th className="text-left p-3 w-32">Would unlock</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {(data.top_questions || []).map((q, i) => {
                        const rt = Object.entries(q.required_tiers || {}).sort((a,b)=>b[1]-a[1])[0]?.[0] || '—';
                        return (
                          <tr key={i} data-testid={`gate-hit-row-${i}`}>
                            <td className="p-3">
                              <div className="font-semibold truncate max-w-[520px]">{q.sample_question}</div>
                              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mt-0.5">
                                {Object.keys(q.topics || {}).join(' · ')}
                              </div>
                            </td>
                            <td className="p-3 text-right font-mono font-bold">{q.count}</td>
                            <td className="p-3 text-right font-mono">{q.unique_users}</td>
                            <td className="p-3">
                              <span className="inline-flex items-center rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-amber-500/15 text-amber-700 dark:text-amber-300">
                                {rt}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* MRR breakdown by required tier */}
            <section className="mb-6">
              <h2 className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
                MRR opportunity by required tier
              </h2>
              <div className="grid gap-3 sm:grid-cols-3">
                {['pro', 'business', 'enterprise'].map((t) => {
                  const row = data.mrr_left_by_tier?.[t];
                  return (
                    <div key={t} className="pro-card p-4" data-testid={`mrr-${t}`}>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{t}</div>
                      {row ? (
                        <>
                          <div className="font-heading text-2xl font-extrabold mt-1">{money(row.estimated_mrr_at_5pct)}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {row.unique_gated_users} users @ {money(row.monthly_price)}/mo · potential {money(row.potential_mrr)}
                          </div>
                        </>
                      ) : (
                        <div className="text-xs text-muted-foreground mt-1">No gate hits requiring {t}.</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default AIGateHits;
